/**
 * HRPage.tsx
 * HR Portal — HR Docs, Employee Reviews, Training Records, Health Care, Financial tabs.
 * Spell check enabled on all text inputs (spellCheck + lang="en").
 *
 * Place at: frontend/src/pages/HRPage.tsx
 */
import React, { useState, useEffect, useRef } from "react";

// ─── Inline SVG Icons ─────────────────────────────────────────────────────────
const Icon = ({ d, size = 18, color = "currentColor", style = {} }: any) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
    <path d={d} />
  </svg>
);
const Ic = {
  Users:      (p: any) => <Icon d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2 M9 11a4 4 0 100-8 4 4 0 000 8z M23 21v-2a4 4 0 00-3-3.87 M16 3.13a4 4 0 010 7.75" {...p}/>,
  File:       (p: any) => <Icon d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z M14 2v6h6" {...p}/>,
  FileText:   (p: any) => <Icon d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8" {...p}/>,
  Star:       (p: any) => <Icon d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" {...p}/>,
  Award:      (p: any) => <Icon d="M12 15a7 7 0 100-14 7 7 0 000 14z M8.21 13.89L7 23l5-3 5 3-1.21-9.12" {...p}/>,
  Heart:      (p: any) => <Icon d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" {...p}/>,
  DollarSign: (p: any) => <Icon d="M12 1v22 M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" {...p}/>,
  Plus:       (p: any) => <Icon d="M12 5v14 M5 12h14" {...p}/>,
  Search:     (p: any) => <Icon d="M11 19a8 8 0 100-16 8 8 0 000 16z M21 21l-4.35-4.35" {...p}/>,
  Upload:     (p: any) => <Icon d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4 M17 8l-5-5-5 5 M12 3v12" {...p}/>,
  Download:   (p: any) => <Icon d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4 M7 10l5 5 5-5 M12 15V3" {...p}/>,
  Eye:        (p: any) => <Icon d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z M12 9a3 3 0 100 6 3 3 0 000-6z" {...p}/>,
  Edit:       (p: any) => <Icon d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7 M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" {...p}/>,
  Check:      (p: any) => <Icon d="M20 6L9 17l-5-5" {...p}/>,
  X:          (p: any) => <Icon d="M18 6L6 18 M6 6l12 12" {...p}/>,
  Alert:      (p: any) => <Icon d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z M12 9v4 M12 17h.01" {...p}/>,
  ChevronRight: (p: any) => <Icon d="M9 18l6-6-6-6" {...p}/>,
  RotateCcw:  (p: any) => <Icon d="M1 4v6h6 M3.51 15a9 9 0 102.13-9.36L1 10" {...p}/>,
  Send:       (p: any) => <Icon d="M22 2L11 13 M22 2l-7 20-4-9-9-4 20-7z" {...p}/>,
  Clipboard:  (p: any) => <Icon d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2 M9 2h6a1 1 0 011 1v1a1 1 0 01-1 1H9a1 1 0 01-1-1V3a1 1 0 011-1z" {...p}/>,
  Trash:      (p: any) => <Icon d="M3 6h18 M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6 M10 11v6 M14 11v6 M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" {...p}/>,
};

// ─── Theme ────────────────────────────────────────────────────────────────────
const darkTheme = {
  bg: "#0B1120", surface: "#111827", surfaceHover: "#1a2332",
  border: "#1E293B", borderLight: "#334155",
  text: "#E2E8F0", textMuted: "#94A3B8", textDim: "#64748B",
  accent: "#3B82F6", accentGlow: "rgba(59,130,246,0.15)",
  green: "#10B981", yellow: "#F59E0B", red: "#EF4444",
  purple: "#8B5CF6", orange: "#F97316",
};
const lightTheme = {
  bg: "#F1F5F9", surface: "#FFFFFF", surfaceHover: "#F8FAFC",
  border: "#E2E8F0", borderLight: "#CBD5E1",
  text: "#1E293B", textMuted: "#475569", textDim: "#94A3B8",
  accent: "#3B82F6", accentGlow: "rgba(59,130,246,0.08)",
  green: "#10B981", yellow: "#F59E0B", red: "#EF4444",
  purple: "#8B5CF6", orange: "#F97316",
};
const getTheme = () => {
  const s = typeof localStorage !== "undefined" ? localStorage.getItem("engportal-theme") : null;
  return s === "light" ? lightTheme : darkTheme;
};

// ─── Subfolder tabs ───────────────────────────────────────────────────────────
const TABS = [
  { key: "all",        label: "All",                Icon: Ic.FileText,   color: "#6366F1" },
  { key: "hr_docs",    label: "Employee Documents", Icon: Ic.FileText,   color: "#3B82F6" },
  { key: "reviews",    label: "Employee Reviews",   Icon: Ic.Star,       color: "#F59E0B" },
  { key: "training",   label: "Training Records",   Icon: Ic.Award,      color: "#10B981" },
  { key: "healthcare", label: "Health Care",        Icon: Ic.Heart,      color: "#EF4444" },
  { key: "financial",  label: "Financial",          Icon: Ic.DollarSign, color: "#8B5CF6" },
  { key: "other_benefits", label: "Other Benefits", Icon: Ic.Clipboard,  color: "#14B8A6" },
];

// ─── Rating scale ─────────────────────────────────────────────────────────────
const RATING_OPTIONS = [
  { value: 5, label: "5 – Exceeds Standards" },
  { value: 4, label: "4 – Often Exceeds" },
  { value: 3, label: "3 – At Standard" },
  { value: 2, label: "2 – Often At Standard" },
  { value: 1, label: "1 – Needs Development" },
  { value: 0, label: "N/A" },
];

const ENGINEER_COMPETENCIES = [
  { key: "comp_accountability",     label: "Accountability" },
  { key: "comp_bias_for_action",    label: "Bias for Action" },
  { key: "comp_business_acumen",    label: "Business Acumen" },
  { key: "comp_commitment",         label: "Commitment" },
  { key: "comp_customer_focus",     label: "Customer Focus" },
  { key: "comp_people_development", label: "People Development" },
  { key: "comp_innovation",         label: "Innovation" },
  { key: "comp_leadership",         label: "Leadership" },
  { key: "comp_performance",        label: "Performance" },
  { key: "comp_teamwork",           label: "Teamwork" },
  { key: "comp_values_ethics",      label: "Values and Ethics" },
  { key: "comp_vision_purpose",     label: "Vision and Purpose" },
];

const TECHNICIAN_COMPETENCIES = [
  { key: "comp_accuracy_of_work",    label: "Accuracy of Work",    hint: "How accurate is the work completed by the employee?" },
  { key: "comp_adaptability",        label: "Adaptability",         hint: "How well does the employee handle a change in priorities / tasks?" },
  { key: "comp_attention_to_detail", label: "Attention to Detail",  hint: "Does the employee notice and report/record small issues?" },
  { key: "comp_commitment",          label: "Commitment",           hint: "Is the employee committed to getting the job done?" },
  { key: "comp_communication",       label: "Communication",        hint: "How quickly and accurately does the employee convey issues / problems?" },
  { key: "comp_dependability",       label: "Dependability",        hint: "Is the employee on time and reliable?" },
  { key: "comp_independence",        label: "Independence",         hint: "Can the employee manage their own time & projects?" },
  { key: "comp_performance",         label: "Performance",          hint: "How well does the employee execute their tasks?" },
  { key: "comp_problem_solving",     label: "Problem Solving",      hint: "Can the employee identify issues and offer suggestions to repair/fix?" },
  { key: "comp_teamwork",            label: "Teamwork",             hint: "How well does the employee work with others?" },
  { key: "comp_values_ethics",       label: "Values and Ethics",    hint: "Does the employee always \"do the right thing\"?" },
  { key: "comp_versatility",         label: "Versatility",          hint: "How many different types of jobs can the employee execute well?" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getCookie(name: string): string {
  let val = "";
  if (document.cookie) document.cookie.split(";").forEach(c => {
    const t = c.trim();
    if (t.startsWith(name + "=")) val = decodeURIComponent(t.slice(name.length + 1));
  });
  return val;
}
async function api(url: string, opts: RequestInit = {}) {
  const { headers: extra, ...rest } = opts as any;
  const res = await fetch(url, {
    headers: { "X-CSRFToken": getCookie("csrftoken"), ...(extra || {}) },
    credentials: "include", ...rest,
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { const d = await res.json(); msg = d.error || d.detail || JSON.stringify(d); } catch {}
    throw new Error(msg);
  }
  if (res.status === 204) return null;
  return res.json();
}
function fmtDate(s?: string | null): string {
  if (!s) return "—";
  return new Date(s).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}
function scoreColor(score: number | null, theme: any): string {
  if (score === null) return theme.textDim;
  if (score >= 4.5) return theme.green;
  if (score >= 3.5) return "#10B981";
  if (score >= 2.5) return theme.yellow;
  if (score >= 1.5) return theme.orange;
  return theme.red;
}
function scoreLabel(score: number | null): string {
  if (score === null) return "—";
  if (score >= 4.5) return "Exceptional";
  if (score >= 3.5) return "Excellent";
  if (score >= 2.5) return "Good";
  if (score >= 1.5) return "Fair";
  return "Needs Improvement";
}
// Spell-check enabled input style
const spellProps = { spellCheck: true, lang: "en" };
const inputS = (theme: any): React.CSSProperties => ({
  width: "100%", padding: "9px 12px", background: theme.bg,
  border: `1px solid ${theme.border}`, borderRadius: 8,
  color: theme.text, fontSize: 13, outline: "none", boxSizing: "border-box",
});
function StatusBadge({ status, theme }: { status: string; theme: any }) {
  const colors: Record<string, string> = {
    draft: theme.textDim, submitted: theme.yellow, qm_review: theme.accent,
    returned: theme.orange, approved: theme.green, finalized: theme.purple,
    active: theme.green, archive: theme.textDim,
    pending: theme.yellow, completed: theme.green, expired: theme.red, waived: theme.textDim,
  };
  const labels: Record<string, string> = {
    draft: "Draft", submitted: "Submitted", qm_review: "QM Review",
    returned: "Returned", approved: "Approved", finalized: "Finalized",
    active: "Active", archive: "Archive",
    pending: "Pending", completed: "Completed", expired: "Expired", waived: "Waived",
  };
  const c = colors[status] || theme.textDim;
  return (
    <span style={{ background: c + "22", color: c, fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10, whiteSpace: "nowrap" as const }}>
      {labels[status] || status}
    </span>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function HRPage({ currentUser }: { currentUser?: any }) {
  const theme = getTheme();
  const [activeTab, setActiveTab] = useState("all");
  const [error, setError] = useState("");
  const isManager = currentUser?.role === 'engineering_manager' || currentUser?.role === 'executive';
  const isHRDirector = currentUser?.role === 'hr_director';
  const canUpload = isManager || isHRDirector;
  const canReviews = isManager || isHRDirector;
  const visibleTabs = TABS.filter(t => t.key !== 'reviews' || canReviews);

  const tabMeta = TABS.find(t => t.key === activeTab)!;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: theme.bg }}>
      {/* Top bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 20px", borderBottom: `1px solid ${theme.border}`, flexShrink: 0 }}>
        <Ic.Users size={20} color={theme.accent} />
        <h2 style={{ margin: 0, color: theme.text, fontSize: 18, fontWeight: 600 }}>HR Portal</h2>
      </div>

      {error && (
        <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid #EF4444", borderRadius: 8, margin: "8px 16px", padding: "9px 12px", color: "#EF4444", fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
          <Ic.Alert size={13} /> {error}
          <button onClick={() => setError("")} style={{ marginLeft: "auto", background: "none", border: "none", color: "#EF4444", cursor: "pointer", fontSize: 18 }}>×</button>
        </div>
      )}

      {/* Tab bar */}
      <div style={{ display: "flex", borderBottom: `1px solid ${theme.border}`, padding: "0 20px", background: theme.surface, flexShrink: 0, overflowX: "auto" }}>
        {visibleTabs.map(t => {
          const isActive = activeTab === t.key;
          return (
            <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
              display: "flex", alignItems: "center", gap: 7, padding: "11px 16px",
              background: "none", border: "none",
              borderBottom: `2px solid ${isActive ? t.color : "transparent"}`,
              color: isActive ? t.color : theme.textMuted,
              fontSize: 13, fontWeight: isActive ? 600 : 500, cursor: "pointer",
              transition: "all 0.15s", marginBottom: -1, whiteSpace: "nowrap" as const,
            }}
              onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLElement).style.color = theme.text; }}
              onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLElement).style.color = theme.textMuted; }}
            >
              <t.Icon size={14} color={isActive ? t.color : undefined} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div style={{ flex: 1, overflow: "hidden" }}>
        {activeTab === "all"      && <AllTab theme={theme} isManager={canUpload} currentUser={currentUser} onError={setError} />}
        {activeTab === "hr_docs"  && <HRDocsTab theme={theme} isManager={canUpload} currentUser={currentUser} onError={setError} category="hr_docs" label="Employee Documents" />}
        {activeTab === "reviews"  && canReviews && <ReviewsTab theme={theme} isManager={isManager} isHRDirector={isHRDirector} currentUser={currentUser} onError={setError} />}
        {activeTab === "training" && <TrainingTab theme={theme} isManager={isManager} isHRDirector={isHRDirector} currentUser={currentUser} onError={setError} />}
        {activeTab === "healthcare" && <HRDocsTab theme={theme} isManager={canUpload} currentUser={currentUser} onError={setError} category="healthcare" label="Health Care" />}
        {activeTab === "financial"  && <HRDocsTab theme={theme} isManager={canUpload} currentUser={currentUser} onError={setError} category="financial" label="Financial" />}
        {activeTab === "other_benefits" && <HRDocsTab theme={theme} isManager={canUpload} currentUser={currentUser} onError={setError} category="other_benefits" label="Other Benefits" />}
      </div>
    </div>
  );
}

// ─── Placeholder Tab ──────────────────────────────────────────────────────────
function PlaceholderTab({ theme, label, icon, message }: any) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 16, padding: 40 }}>
      {icon}
      <div style={{ color: theme.text, fontSize: 18, fontWeight: 600 }}>{label}</div>
      <div style={{ color: theme.textDim, fontSize: 14, textAlign: "center" as const, maxWidth: 400 }}>{message}</div>
      <div style={{ color: theme.textDim, fontSize: 12 }}>Coming soon — coordinate with HR director to configure</div>
    </div>
  );
}

// ─── Sortable table helpers ──────────────────────────────────────────────────
function sortRows(rows: any[], key: string, dir: string) {
  if (!key) return rows;
  const out = [...rows];
  out.sort((a, b) => {
    let av = a[key]; let bv = b[key];
    av = av == null ? "" : (typeof av === "string" ? av.toLowerCase() : av);
    bv = bv == null ? "" : (typeof bv === "string" ? bv.toLowerCase() : bv);
    if (av < bv) return dir === "asc" ? -1 : 1;
    if (av > bv) return dir === "asc" ? 1 : -1;
    return 0;
  });
  return out;
}
function SortableTh({ label, colKey, sortKey, sortDir, onSort, theme }: any) {
  const active = sortKey === colKey;
  return (
    <th onClick={() => onSort(colKey)} style={{ textAlign: "left", padding: "8px 10px", color: active ? theme.text : theme.textMuted, fontWeight: 600, fontSize: 11, textTransform: "uppercase" as const, letterSpacing: 0.4, cursor: "pointer", userSelect: "none" as const, whiteSpace: "nowrap" as const, borderBottom: `1px solid ${theme.border}` }}>
      {label}{active ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
    </th>
  );
}
function DocActionLinks({ doc, theme }: any) {
  if (!doc.file) return <span style={{ color: theme.textDim, fontSize: 12 }}>—</span>;
  return (
    <div style={{ display: "flex", gap: 4 }}>
      <a href={`/api/hr/documents/${doc.id}/view/`} target="_blank" rel="noopener noreferrer" title="View" onClick={e => e.stopPropagation()}
        style={{ width: 28, height: 28, borderRadius: 6, background: theme.surface, border: `1px solid ${theme.border}`, color: theme.textMuted, display: "inline-flex", alignItems: "center", justifyContent: "center", textDecoration: "none" }}>
        <Ic.Eye size={13} />
      </a>
      <a href={`/api/hr/documents/${doc.id}/download/`} download title="Download" onClick={e => e.stopPropagation()}
        style={{ width: 28, height: 28, borderRadius: 6, background: theme.surface, border: `1px solid ${theme.border}`, color: theme.textMuted, display: "inline-flex", alignItems: "center", justifyContent: "center", textDecoration: "none" }}>
        <Ic.Download size={13} />
      </a>
    </div>
  );
}
const FOLDER_LABELS: any = { hr_docs: "Employee Documents", healthcare: "Health Care", financial: "Financial", other_benefits: "Other Benefits", reviews: "Employee Reviews", training: "Training Records" };

// ─── HR Docs Tab (category-driven, sortable) ─────────────────────────────────
function HRDocsTab({ theme, isManager, currentUser, onError, category = "hr_docs", label = "Employee Documents" }: any) {
  const [docs, setDocs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [sortKey, setSortKey] = useState("import_date");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => { load(); }, [category]);
  async function load() {
    setLoading(true);
    try {
      const d = await api(`/api/hr/documents/?category=${category}`);
      setDocs(Array.isArray(d) ? d : d?.results || []);
    } catch (e: any) { onError(e.message); }
    finally { setLoading(false); }
  }
  const onSort = (k: string) => { if (sortKey === k) setSortDir(d => (d === "asc" ? "desc" : "asc")); else { setSortKey(k); setSortDir("asc"); } };
  const filtered = docs.filter(d => !search || (d.title || "").toLowerCase().includes(search.toLowerCase()) || (d.system_doc_number || "").toLowerCase().includes(search.toLowerCase()));
  const rows = sortRows(filtered, sortKey, sortDir);

  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden" }}>
      <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 8, padding: "7px 10px", flex: 1 }}>
            <Ic.Search size={13} color={theme.textDim} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder={`Search ${label}…`}
              style={{ background: "none", border: "none", color: theme.text, fontSize: 13, outline: "none", flex: 1 }} {...spellProps} />
          </div>
          {isManager && (
            <button onClick={() => setShowUpload(true)} style={{ display: "flex", alignItems: "center", gap: 6, background: "#3B82F6", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              <Ic.Upload size={14} /> Upload
            </button>
          )}
        </div>
        {loading ? <div style={{ color: theme.textDim, textAlign: "center", paddingTop: 40 }}>Loading…</div>
          : rows.length === 0 ? <EmptyState theme={theme} label={label} icon={<Ic.FileText size={36} color="#3B82F6" />} />
          : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead><tr>
                <SortableTh label="Title" colKey="title" sortKey={sortKey} sortDir={sortDir} onSort={onSort} theme={theme} />
                <SortableTh label="Doc #" colKey="system_doc_number" sortKey={sortKey} sortDir={sortDir} onSort={onSort} theme={theme} />
                <SortableTh label="Rev" colKey="revision" sortKey={sortKey} sortDir={sortDir} onSort={onSort} theme={theme} />
                <SortableTh label="Date" colKey="import_date" sortKey={sortKey} sortDir={sortDir} onSort={onSort} theme={theme} />
                <SortableTh label="Status" colKey="status" sortKey={sortKey} sortDir={sortDir} onSort={onSort} theme={theme} />
                <th style={{ borderBottom: `1px solid ${theme.border}` }}></th>
              </tr></thead>
              <tbody>
                {rows.map(doc => (
                  <tr key={doc.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                    <td style={{ padding: "10px", color: theme.text, fontWeight: 600 }}>{doc.title}</td>
                    <td style={{ padding: "10px", color: theme.textDim, fontSize: 12 }}>{doc.system_doc_number}</td>
                    <td style={{ padding: "10px", color: theme.textDim, fontSize: 12 }}>{doc.revision}</td>
                    <td style={{ padding: "10px", color: theme.textDim, fontSize: 12 }}>{fmtDate(doc.import_date)}</td>
                    <td style={{ padding: "10px" }}><StatusBadge status={doc.status} theme={theme} /></td>
                    <td style={{ padding: "10px" }}><DocActionLinks doc={doc} theme={theme} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        }
      </div>
      {showUpload && isManager && (
        <HRDocUploadModal theme={theme} category={category} onClose={() => setShowUpload(false)}
          onSuccess={(d: any) => { setDocs(p => [d, ...p]); setShowUpload(false); }} />
      )}
    </div>
  );
}

// ─── All Tab (documents across folders + training records, sortable) ─────────
function AllTab({ theme, isManager, currentUser, onError }: any) {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => { load(); }, []);
  async function load() {
    setLoading(true);
    try {
      const [docs, training] = await Promise.all([
        api("/api/hr/documents/").catch(() => []),
        api("/api/hr/training/").catch(() => []),
      ]);
      const docList = (Array.isArray(docs) ? docs : docs?.results || []).map((d: any) => ({
        id: "doc-" + d.id, kind: "Document", name: d.title, folder: FOLDER_LABELS[d.category] || d.category || "Documents",
        reference: d.system_doc_number, person: d.owner_name || d.uploaded_by_name || "—", date: d.import_date, status: d.status, raw: d,
      }));
      const trList = (Array.isArray(training) ? training : training?.results || []).map((t: any) => ({
        id: "tr-" + t.id, kind: "Training", name: t.training_name, folder: "Training Records",
        reference: t.system_doc_number, person: t.employee_name || "—", date: t.completed_date, status: t.status, raw: t,
      }));
      setRows([...docList, ...trList]);
    } catch (e: any) { onError(e.message); }
    finally { setLoading(false); }
  }
  const onSort = (k: string) => { if (sortKey === k) setSortDir(d => (d === "asc" ? "desc" : "asc")); else { setSortKey(k); setSortDir("asc"); } };
  const filtered = rows.filter(r => !search || (r.name || "").toLowerCase().includes(search.toLowerCase()) || (r.reference || "").toLowerCase().includes(search.toLowerCase()) || (r.folder || "").toLowerCase().includes(search.toLowerCase()));
  const sorted = sortRows(filtered, sortKey, sortDir);

  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden" }}>
      <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 8, padding: "7px 10px", flex: 1 }}>
            <Ic.Search size={13} color={theme.textDim} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search all HR documents & training…"
              style={{ background: "none", border: "none", color: theme.text, fontSize: 13, outline: "none", flex: 1 }} {...spellProps} />
          </div>
          {isManager && (
            <button onClick={() => setShowUpload(true)} style={{ display: "flex", alignItems: "center", gap: 6, background: "#3B82F6", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              <Ic.Upload size={14} /> Upload
            </button>
          )}
        </div>
        {loading ? <div style={{ color: theme.textDim, textAlign: "center", paddingTop: 40 }}>Loading…</div>
          : sorted.length === 0 ? <EmptyState theme={theme} label="HR Records" icon={<Ic.FileText size={36} color="#6366F1" />} />
          : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead><tr>
                <SortableTh label="Name" colKey="name" sortKey={sortKey} sortDir={sortDir} onSort={onSort} theme={theme} />
                <SortableTh label="Folder" colKey="folder" sortKey={sortKey} sortDir={sortDir} onSort={onSort} theme={theme} />
                <SortableTh label="Reference" colKey="reference" sortKey={sortKey} sortDir={sortDir} onSort={onSort} theme={theme} />
                <SortableTh label="Person" colKey="person" sortKey={sortKey} sortDir={sortDir} onSort={onSort} theme={theme} />
                <SortableTh label="Date" colKey="date" sortKey={sortKey} sortDir={sortDir} onSort={onSort} theme={theme} />
                <SortableTh label="Status" colKey="status" sortKey={sortKey} sortDir={sortDir} onSort={onSort} theme={theme} />
                <th style={{ borderBottom: `1px solid ${theme.border}` }}></th>
              </tr></thead>
              <tbody>
                {sorted.map(r => (
                  <tr key={r.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                    <td style={{ padding: "10px", color: theme.text, fontWeight: 600 }}>{r.name}</td>
                    <td style={{ padding: "10px" }}><span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10, background: r.kind === "Training" ? "#10B98118" : "#3B82F618", color: r.kind === "Training" ? "#10B981" : "#3B82F6" }}>{r.folder}</span></td>
                    <td style={{ padding: "10px", color: theme.textDim, fontSize: 12 }}>{r.reference}</td>
                    <td style={{ padding: "10px", color: theme.textDim, fontSize: 12 }}>{r.person}</td>
                    <td style={{ padding: "10px", color: theme.textDim, fontSize: 12 }}>{fmtDate(r.date)}</td>
                    <td style={{ padding: "10px" }}><StatusBadge status={r.status} theme={theme} /></td>
                    <td style={{ padding: "10px" }}>{r.kind === "Document" ? <DocActionLinks doc={r.raw} theme={theme} /> : <span style={{ color: theme.textDim, fontSize: 12 }}>—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        }
      </div>
      {showUpload && isManager && (
        <HRDocUploadModal theme={theme} category="hr_docs" allowFolderChoice onClose={() => setShowUpload(false)}
          onSuccess={() => { setShowUpload(false); load(); }} />
      )}
    </div>
  );
}

function DocRow({ doc, theme, onSelect }: any) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 10, marginBottom: 8, transition: "border-color 0.15s" }}
      onMouseEnter={e => (e.currentTarget as HTMLElement).style.borderColor = theme.borderLight}
      onMouseLeave={e => (e.currentTarget as HTMLElement).style.borderColor = theme.border}
    >
      <Ic.FileText size={16} color="#3B82F6" />
      <div onClick={onSelect} style={{ flex: 1, minWidth: 0, cursor: "pointer" }}>
        <div style={{ color: theme.text, fontSize: 14, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{doc.title}</div>
        <div style={{ color: theme.textDim, fontSize: 11, marginTop: 2 }}>{doc.system_doc_number} · Rev {doc.revision} · {fmtDate(doc.import_date)}</div>
      </div>
      <StatusBadge status={doc.status} theme={theme} />
      <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
        {doc.file && (
          <>
            <a href={`/api/hr/documents/${doc.id}/view/`} target="_blank" rel="noopener noreferrer"
              title="View" onClick={e => e.stopPropagation()}
              style={{ width: 30, height: 30, borderRadius: 6, background: theme.surface, border: `1px solid ${theme.border}`, color: theme.textMuted, display: "inline-flex", alignItems: "center", justifyContent: "center", textDecoration: "none" }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = "#3B82F6"; (e.currentTarget as HTMLElement).style.borderColor = "#3B82F6"; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = theme.textMuted; (e.currentTarget as HTMLElement).style.borderColor = theme.border; }}>
              <Ic.Eye size={14} />
            </a>
            <a href={`/api/hr/documents/${doc.id}/download/`} download title="Download"
              onClick={e => e.stopPropagation()}
              style={{ width: 30, height: 30, borderRadius: 6, background: theme.surface, border: `1px solid ${theme.border}`, color: theme.textMuted, display: "inline-flex", alignItems: "center", justifyContent: "center", textDecoration: "none" }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = "#10B981"; (e.currentTarget as HTMLElement).style.borderColor = "#10B981"; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = theme.textMuted; (e.currentTarget as HTMLElement).style.borderColor = theme.border; }}>
              <Ic.Download size={14} />
            </a>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Reviews Tab ──────────────────────────────────────────────────────────────
function ReviewsTab({ theme, isManager, isHRDirector, currentUser, onError }: any) {
  const [reviews, setReviews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => { load(); }, [statusFilter]);

  async function load() {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : "";
      const d = await api(`/api/hr/reviews/${params}`);
      setReviews(Array.isArray(d) ? d : d?.results || []);
    } catch (e: any) { onError(e.message); }
    finally { setLoading(false); }
  }

  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden" }}>
      {/* List */}
      <div style={{ width: selected ? 320 : "100%", borderRight: selected ? `1px solid ${theme.border}` : "none", display: "flex", flexDirection: "column", overflow: "hidden", transition: "width 0.2s" }}>
        <div style={{ padding: "14px 16px", borderBottom: `1px solid ${theme.border}`, display: "flex", alignItems: "center", gap: 8 }}>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            style={{ ...inputS(theme), width: "auto", flex: 1 }}>
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="returned">Returned</option>
            <option value="approved">Approved</option>
          </select>
          {isManager && (
            <button onClick={() => setShowNew(true)} style={{ display: "flex", alignItems: "center", gap: 5, background: theme.yellow, color: "#000", border: "none", borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
              <Ic.Plus size={13} /> New Review
            </button>
          )}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: 12 }}>
          {loading ? <div style={{ color: theme.textDim, textAlign: "center", paddingTop: 40 }}>Loading…</div>
            : reviews.length === 0 ? <EmptyState theme={theme} label="Employee Reviews" icon={<Ic.Star size={36} color="#F59E0B" />} />
            : reviews.map(r => (
              <div key={r.id} onClick={() => setSelected(r)}
                style={{ padding: "12px 14px", background: selected?.id === r.id ? theme.accentGlow : theme.surface, border: `1px solid ${selected?.id === r.id ? theme.accent : theme.border}`, borderRadius: 10, marginBottom: 8, cursor: "pointer", transition: "all 0.12s" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ color: theme.text, fontSize: 13, fontWeight: 600 }}>{r.employee_name}</span>
                  <StatusBadge status={r.status} theme={theme} />
                </div>
                <div style={{ color: theme.textDim, fontSize: 11 }}>
                  {r.review_type_label} · {r.review_year} · {r.system_doc_number}
                </div>
                {r.overall_score && (
                  <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ color: theme.textDim, fontSize: 11 }}>Score:</span>
                    <span style={{ color: scoreColor(r.overall_score, theme), fontSize: 12, fontWeight: 700 }}>{r.overall_score.toFixed(1)}</span>
                    <span style={{ color: theme.textDim, fontSize: 11 }}>— {scoreLabel(r.overall_score)}</span>
                  </div>
                )}
              </div>
            ))
          }
        </div>
      </div>

      {/* Detail panel */}
      {selected && (
        <div style={{ flex: 1, overflowY: "auto" }}>
          <ReviewDetailPanel
            review={selected} theme={theme} isManager={isManager} isHRDirector={isHRDirector} currentUser={currentUser}
            onClose={() => setSelected(null)}
            onUpdate={(updated: any) => {
              setReviews(p => p.map(r => r.id === updated.id ? updated : r));
              setSelected(updated);
            }}
            onError={onError}
          />
        </div>
      )}

      {showNew && (
        <NewReviewModal theme={theme} currentUser={currentUser} onClose={() => setShowNew(false)}
          onSuccess={(r: any) => { setReviews(p => [r, ...p]); setShowNew(false); setSelected(r); }} />
      )}
    </div>
  );
}

// ─── Review Detail Panel ──────────────────────────────────────────────────────
function ReviewDetailPanel({ review, theme, isManager, isHRDirector, currentUser, onClose, onUpdate, onError }: any) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<any>({ ...review });
  const [qmNotes, setQmNotes] = useState(review.qm_notes || "");
  const [busy, setBusy] = useState(false);

  const competencies = review.review_type === 'engineer' ? ENGINEER_COMPETENCIES : TECHNICIAN_COMPETENCIES;
  const canEdit = review.reviewer === currentUser?.id && ['draft', 'returned'].includes(review.status);
  const canSubmit = review.reviewer === currentUser?.id && ['draft', 'returned'].includes(review.status);
  const canQMAction = isHRDirector && ['submitted', 'qm_review'].includes(review.status);
  const isReturned = review.status === 'returned';

  function set(field: string, val: any) {
    setForm((p: any) => ({ ...p, [field]: val }));
  }

  async function save() {
    setBusy(true);
    try {
      const updated = await api(`/api/hr/reviews/${review.id}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      onUpdate(updated);
      setEditing(false);
    } catch (e: any) { onError(e.message); }
    finally { setBusy(false); }
  }

  async function submit() {
    setBusy(true);
    try {
      const updated = await api(`/api/hr/reviews/${review.id}/submit/`, { method: "POST" });
      onUpdate(updated);
    } catch (e: any) { onError(e.message); }
    finally { setBusy(false); }
  }

  async function qmApprove() {
    setBusy(true);
    try {
      const updated = await api(`/api/hr/reviews/${review.id}/qm_approve/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ qm_notes: qmNotes }),
      });
      onUpdate(updated);
    } catch (e: any) { onError(e.message); }
    finally { setBusy(false); }
  }

  async function qmReturn() {
    setBusy(true);
    try {
      const payload = { ...form, qm_notes: qmNotes };
      const updated = await api(`/api/hr/reviews/${review.id}/qm_return/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      onUpdate(updated);
    } catch (e: any) { onError(e.message); }
    finally { setBusy(false); }
  }

  function printReview() {
    const r = review;
    const esc = (v: any) => String(v == null ? "" : v)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
    const ratingLabel = (v: any) => {
      if (v == null || v === "") return "—";
      const o = RATING_OPTIONS.find((x: any) => x.value === v);
      return o ? esc(o.label) : esc(v);
    };
    const compRows = competencies.map((c: any) => {
      const comment = (r.competency_comments && r.competency_comments[c.key]) || "";
      return `<tr><td class="lbl">${esc(c.label)}</td><td class="ctr">${ratingLabel(r[c.key])}</td><td>${esc(comment)}</td></tr>`;
    }).join("");
    const goals = Array.isArray(r.goals) ? r.goals : [];
    const goalRows = goals.length ? goals.map((g: any, i: number) =>
      `<tr><td class="ctr">${i + 1}</td><td>${esc(g.dept_goal)}</td><td>${esc(g.goal)}</td><td>${esc(g.measure)}</td><td>${esc(g.target)}</td><td>${esc(g.results)}</td><td class="ctr">${g.results_score ? esc(g.results_score) : "—"}</td></tr>`
    ).join("") : `<tr><td colspan="7" class="empty">No goals recorded.</td></tr>`;
    const tp = Array.isArray(r.training_plan) ? r.training_plan : [];
    const tpRows = tp.length ? tp.map((t: any) =>
      `<tr><td>${esc(t.action_steps)}</td><td>${esc(t.time_frame)}</td><td>${esc(t.manager_support)}</td></tr>`
    ).join("") : `<tr><td colspan="3" class="empty">No development items recorded.</td></tr>`;
    const sigBlock = (role: string) =>
      `<div class="sig"><div class="sigline"></div><div class="siglabel">${esc(role)} — Signature</div></div><div class="sig sm"><div class="sigline"></div><div class="siglabel">Date</div></div>`;
    const overall = r.overall_score != null ? Number(r.overall_score).toFixed(1) : "—";

    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(r.system_doc_number || "Employee Review")}</title>
<style>
  *{box-sizing:border-box;} body{font-family:Arial,Helvetica,sans-serif;color:#111;margin:32px;font-size:12px;line-height:1.4;}
  h1{font-size:18px;margin:0 0 2px;} h2{font-size:13px;margin:18px 0 6px;border-bottom:2px solid #333;padding-bottom:3px;text-transform:uppercase;letter-spacing:.5px;}
  .sub{color:#555;font-size:11px;margin-bottom:12px;}
  table{width:100%;border-collapse:collapse;margin-bottom:6px;} th,td{border:1px solid #999;padding:5px 7px;text-align:left;vertical-align:top;}
  th{background:#eee;font-size:11px;} td.ctr,th.ctr{text-align:center;} td.lbl{font-weight:bold;width:22%;} td.empty{color:#888;font-style:italic;text-align:center;}
  .meta{width:100%;margin-bottom:4px;} .meta td{border:none;padding:2px 6px;} .meta td.k{color:#555;width:120px;} .meta td.v{font-weight:bold;}
  .scorebox{display:inline-block;border:1px solid #999;padding:6px 14px;margin-top:6px;font-size:13px;}
  .free{border:1px solid #999;padding:8px;min-height:40px;white-space:pre-wrap;margin-bottom:6px;}
  .sigs{margin-top:36px;display:flex;flex-wrap:wrap;gap:26px 40px;} .sig{flex:1 1 280px;} .sig.sm{flex:0 0 150px;}
  .sigline{border-bottom:1px solid #000;height:30px;} .siglabel{font-size:10px;color:#333;margin-top:3px;}
  .note{font-size:10px;color:#555;margin-top:14px;}
  @media print{body{margin:14mm;} h2{page-break-after:avoid;} table,.sig{page-break-inside:avoid;}}
</style></head><body>
  <h1>AvCarb — Employee Performance Review</h1>
  <div class="sub">${esc(r.system_doc_number || "")} &nbsp;·&nbsp; ${esc(r.review_type_label || r.review_type || "")} &nbsp;·&nbsp; ${esc(r.review_year || "")} &nbsp;·&nbsp; Status: ${esc(r.status_label || r.status || "")}</div>
  <table class="meta">
    <tr><td class="k">Employee</td><td class="v">${esc(r.employee_name || "")}</td><td class="k">Title</td><td class="v">${esc(r.employee_title || "")}</td></tr>
    <tr><td class="k">Reviewer</td><td class="v">${esc(r.reviewer_name || "")}</td><td class="k">Start Date</td><td class="v">${esc(fmtDate(r.employee_start_date))}</td></tr>
  </table>
  <h2>Behavioral Competencies</h2>
  <table><thead><tr><th>Competency</th><th class="ctr">Rating</th><th>Comments</th></tr></thead><tbody>${compRows}</tbody></table>
  <div class="scorebox">Overall Rating: <strong>${overall}</strong> &nbsp; ${esc(scoreLabel(r.overall_score))}</div>
  <h2>Goals</h2>
  <table><thead><tr><th class="ctr">#</th><th>Company / Dept Goal</th><th>Goal</th><th>Measure</th><th>Target</th><th>Results</th><th class="ctr">Score</th></tr></thead><tbody>${goalRows}</tbody></table>
  <h2>Training / Development Plan</h2>
  <table><thead><tr><th>Action Steps</th><th>Time Frame</th><th>Manager / Supervisor Support</th></tr></thead><tbody>${tpRows}</tbody></table>
  ${r.development_opportunities ? `<div class="free">${esc(r.development_opportunities)}</div>` : ""}
  <h2>Reviewer Comments</h2><div class="free">${esc(r.reviewer_comments)}</div>
  <h2>Employee Comments</h2><div class="free">${esc(r.employee_comments)}</div>
  ${r.qm_notes ? `<h2>HR Notes</h2><div class="free">${esc(r.qm_notes)}</div>` : ""}
  <h2>Signatures</h2>
  <div class="sigs">${sigBlock("Reviewer")}${sigBlock("Employee")}${sigBlock("HR Director")}</div>
  <div class="note">Printed ${esc(new Date().toLocaleString())} from the AvCarb Engineering Portal. This printout reflects the review as completed online.</div>
</body></html>`;

    const w = window.open("", "_blank");
    if (!w) { onError("Pop-up blocked — please allow pop-ups for this site to print the review."); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => { try { w.print(); } catch (e) { /* user can print manually */ } }, 350);
  }

  const IS = inputS(theme);

  return (
    <div style={{ padding: 20 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 20, borderBottom: `1px solid ${theme.border}`, paddingBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <span style={{ color: theme.text, fontSize: 17, fontWeight: 700 }}>{review.employee_name}</span>
            <StatusBadge status={review.status} theme={theme} />
          </div>
          <div style={{ color: theme.textDim, fontSize: 12 }}>
            {review.system_doc_number} · {review.review_type_label} · {review.review_year}
            {review.employee_title && ` · ${review.employee_title}`}
            {review.employee_start_date && ` · Started ${fmtDate(review.employee_start_date)}`}
          </div>
          {review.overall_score && (
            <div style={{ marginTop: 8, display: "flex", gap: 16 }}>
              {[
                { label: "Competency", val: review.competency_score },
                { label: "Goals", val: review.goal_score },
                { label: "Overall", val: review.overall_score },
              ].map(s => (
                <div key={s.label} style={{ textAlign: "center" as const }}>
                  <div style={{ color: scoreColor(s.val, theme), fontSize: 20, fontWeight: 700 }}>{s.val?.toFixed(1) || "—"}</div>
                  <div style={{ color: theme.textDim, fontSize: 10 }}>{s.label}</div>
                </div>
              ))}
              <div style={{ textAlign: "center" as const }}>
                <div style={{ color: scoreColor(review.overall_score, theme), fontSize: 13, fontWeight: 600 }}>{scoreLabel(review.overall_score)}</div>
                <div style={{ color: theme.textDim, fontSize: 10 }}>Rating</div>
              </div>
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {canEdit && !editing && <ActionBtn label="Edit" icon={<Ic.Edit size={13} />} onClick={() => setEditing(true)} color={theme.accent} theme={theme} />}
          {editing && <ActionBtn label="Save" icon={<Ic.Check size={13} />} onClick={save} color={theme.green} theme={theme} busy={busy} />}
          {editing && <ActionBtn label="Cancel" icon={<Ic.X size={13} />} onClick={() => { setEditing(false); setForm({ ...review }); }} color={theme.textMuted} theme={theme} />}
          {canSubmit && !editing && <ActionBtn label="Submit for Review" icon={<Ic.Send size={13} />} onClick={submit} color={theme.yellow} theme={theme} busy={busy} />}
          {!editing && <ActionBtn label="Print / Download" icon={<Ic.Download size={13} />} onClick={printReview} color={theme.accent} theme={theme} />}
          <button onClick={onClose} style={{ background: "none", border: "none", color: theme.textDim, cursor: "pointer", fontSize: 20 }}>×</button>
        </div>
      </div>

      {/* Returned banner with diff */}
      {isReturned && review.qm_edits && Object.keys(review.qm_edits).length > 0 && (
        <div style={{ background: theme.orange + "11", border: `1px solid ${theme.orange}`, borderRadius: 10, padding: 14, marginBottom: 16 }}>
          <div style={{ color: theme.orange, fontSize: 13, fontWeight: 600, marginBottom: 8 }}>⚠ Returned by QM — Review the following edits:</div>
          {Object.entries(review.qm_edits).map(([field, diff]: any) => (
            <div key={field} style={{ marginBottom: 6, fontSize: 12 }}>
              <span style={{ color: theme.textMuted, fontWeight: 600 }}>{field.replace(/_/g, ' ')}: </span>
              <span style={{ color: theme.red, textDecoration: "line-through", marginRight: 8 }}>{String(diff.original)}</span>
              <span style={{ color: theme.green }}>→ {String(diff.edited)}</span>
            </div>
          ))}
          {review.qm_notes && <div style={{ color: theme.textMuted, fontSize: 12, marginTop: 8, borderTop: `1px solid ${theme.border}`, paddingTop: 8 }}>QM Notes: {review.qm_notes}</div>}
        </div>
      )}

      {/* Behavioral Competencies */}
      <Section title="Behavioral Competencies" theme={theme}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: theme.bg }}>
                <th style={{ textAlign: "left", padding: "8px 10px", color: theme.textMuted, fontWeight: 600, width: "30%" }}>Competency</th>
                {RATING_OPTIONS.map(r => (
                  <th key={r.value} style={{ textAlign: "center", padding: "8px 6px", color: theme.textMuted, fontWeight: 500, fontSize: 11, width: 70 }}>{r.label.split("–")[0].trim()}</th>
                ))}
                <th style={{ textAlign: "left", padding: "8px 10px", color: theme.textMuted, fontWeight: 600 }}>Comments</th>
              </tr>
            </thead>
            <tbody>
              {competencies.map((comp, i) => {
                const val = form[comp.key];
                return (
                  <tr key={comp.key} style={{ borderTop: `1px solid ${theme.border}`, background: i % 2 === 0 ? "transparent" : theme.bg + "80" }}>
                    <td style={{ padding: "8px 10px", color: theme.text, fontWeight: 500 }}>
                      {comp.label}
                      {(comp as any).hint && <div style={{ color: theme.textDim, fontSize: 11, marginTop: 2 }}>{(comp as any).hint}</div>}
                    </td>
                    {RATING_OPTIONS.map(r => (
                      <td key={r.value} style={{ textAlign: "center", padding: "8px 6px" }}>
                        <input type="radio" name={comp.key} value={r.value}
                          checked={val === r.value} disabled={!editing && !canQMAction}
                          onChange={() => set(comp.key, r.value)}
                          style={{ cursor: editing || canQMAction ? "pointer" : "default", accentColor: theme.accent }} />
                      </td>
                    ))}
                    <td style={{ padding: "6px 10px" }}>
                      <input value={form.competency_comments?.[comp.key] || ""} disabled={!editing && !canQMAction}
                        onChange={e => set("competency_comments", { ...form.competency_comments, [comp.key]: e.target.value })}
                        placeholder="Optional comment…"
                        style={{ ...IS, fontSize: 11, padding: "5px 8px" }} {...spellProps} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Goals */}
      <Section title="Goals" theme={theme}>
        {(form.goals || [{ dept_goal: "", goal: "", measure: "", target: "", results: "", results_score: null }]).map((goal: any, i: number) => (
          <div key={i} style={{ background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 8, padding: 12, marginBottom: 10 }}>
            <div style={{ color: theme.textMuted, fontSize: 11, fontWeight: 600, marginBottom: 8 }}>Goal {i + 1}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {[
                { key: "dept_goal", label: "Company/Dept Goal Supported" },
                { key: "goal", label: "Goal" },
                { key: "measure", label: "Measure" },
                { key: "target", label: "Target" },
                { key: "results", label: "Results" },
              ].map(f => (
                <div key={f.key} style={{ gridColumn: f.key === "results" ? "1/-1" : "auto" }}>
                  <label style={{ color: theme.textDim, fontSize: 11, display: "block", marginBottom: 3 }}>{f.label}</label>
                  <input value={goal[f.key] || ""} disabled={!editing && !canQMAction}
                    onChange={e => {
                      const updated = [...(form.goals || [])];
                      updated[i] = { ...updated[i], [f.key]: e.target.value };
                      set("goals", updated);
                    }}
                    style={IS} {...spellProps} />
                </div>
              ))}
              <div>
                <label style={{ color: theme.textDim, fontSize: 11, display: "block", marginBottom: 3 }}>Results Score (1-5)</label>
                <select value={goal.results_score || ""} disabled={!editing && !canQMAction}
                  onChange={e => {
                    const updated = [...(form.goals || [])];
                    updated[i] = { ...updated[i], results_score: e.target.value ? Number(e.target.value) : null };
                    set("goals", updated);
                  }}
                  style={IS}>
                  <option value="">Select…</option>
                  {[5,4,3,2,1].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </div>
          </div>
        ))}
        {editing && (
          <button onClick={() => set("goals", [...(form.goals || []), { dept_goal: "", goal: "", measure: "", target: "", results: "", results_score: null }])}
            style={{ background: "none", border: `1px dashed ${theme.border}`, borderRadius: 8, color: theme.textMuted, padding: "8px 14px", cursor: "pointer", fontSize: 12, width: "100%" }}>
            + Add Goal
          </button>
        )}
      </Section>

      {/* Training / Development Plan */}
      <Section title="Training / Development Plan" theme={theme}>
        {(form.training_plan || []).map((item: any, i: number) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 8 }}>
            {[
              { key: "action_steps", label: "Action Steps" },
              { key: "time_frame", label: "Time Frame" },
              { key: "manager_support", label: "Manager / Supervisor Support" },
            ].map(f => (
              <div key={f.key}>
                <label style={{ color: theme.textDim, fontSize: 11, display: "block", marginBottom: 3 }}>{f.label}</label>
                <input value={item[f.key] || ""} disabled={!editing && !canQMAction}
                  onChange={e => {
                    const updated = [...(form.training_plan || [])];
                    updated[i] = { ...updated[i], [f.key]: e.target.value };
                    set("training_plan", updated);
                  }}
                  style={IS} {...spellProps} />
              </div>
            ))}
          </div>
        ))}
        {editing && (
          <button onClick={() => set("training_plan", [...(form.training_plan || []), { action_steps: "", time_frame: "", manager_support: "" }])}
            style={{ background: "none", border: `1px dashed ${theme.border}`, borderRadius: 8, color: theme.textMuted, padding: "8px 14px", cursor: "pointer", fontSize: 12, width: "100%" }}>
            + Add Development Item
          </button>
        )}
        <div style={{ marginTop: 10 }}>
          <label style={{ color: theme.textDim, fontSize: 11, display: "block", marginBottom: 4 }}>Development Opportunities May Include:</label>
          <textarea value={form.development_opportunities || ""} disabled={!editing && !canQMAction} rows={2}
            onChange={e => set("development_opportunities", e.target.value)}
            style={{ ...IS, resize: "vertical" as const }} {...spellProps} />
        </div>
      </Section>

      {/* Comments */}
      <Section title="Comments" theme={theme}>
        <div style={{ marginBottom: 12 }}>
          <label style={{ color: theme.textDim, fontSize: 11, display: "block", marginBottom: 4 }}>Reviewer Comments:</label>
          <textarea value={form.reviewer_comments || ""} disabled={!editing && !canQMAction} rows={3}
            onChange={e => set("reviewer_comments", e.target.value)}
            style={{ ...IS, resize: "vertical" as const }} {...spellProps} />
        </div>
        <div>
          <label style={{ color: theme.textDim, fontSize: 11, display: "block", marginBottom: 4 }}>Employee Comments:</label>
          <textarea value={form.employee_comments || ""} disabled={review.employee !== currentUser?.id} rows={3}
            onChange={e => set("employee_comments", e.target.value)}
            style={{ ...IS, resize: "vertical" as const }} {...spellProps} />
        </div>
      </Section>

      {/* QM Actions */}
      {canQMAction && (
        <Section title="QM Review" theme={theme}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ color: theme.textDim, fontSize: 11, display: "block", marginBottom: 4 }}>QM Notes:</label>
            <textarea value={qmNotes} onChange={e => setQmNotes(e.target.value)} rows={3}
              placeholder="Add notes for the reviewer or employee…"
              style={{ ...IS, resize: "vertical" as const }} {...spellProps} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={qmApprove} disabled={busy} style={{ flex: 1, padding: "10px", background: theme.green, border: "none", borderRadius: 8, color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
              ✓ Approve Review
            </button>
            <button onClick={qmReturn} disabled={busy} style={{ flex: 1, padding: "10px", background: theme.orange, border: "none", borderRadius: 8, color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
              ↩ Return for Revision
            </button>
          </div>
        </Section>
      )}

      {/* Edit History */}
      {review.edit_history?.length > 0 && (
        <Section title="Review History" theme={theme}>
          {review.edit_history.map((h: any) => (
            <div key={h.id} style={{ display: "flex", gap: 10, padding: "8px 0", borderBottom: `1px solid ${theme.border}` }}>
              <div style={{ width: 80, color: theme.textDim, fontSize: 11 }}>{fmtDate(h.created_at)}</div>
              <div style={{ flex: 1 }}>
                <span style={{ color: theme.text, fontSize: 12, fontWeight: 600, marginRight: 8 }}>{h.performed_by_name}</span>
                <StatusBadge status={h.action} theme={theme} />
                {h.notes && <div style={{ color: theme.textMuted, fontSize: 11, marginTop: 2 }}>{h.notes}</div>}
              </div>
            </div>
          ))}
        </Section>
      )}
    </div>
  );
}

// ─── Training Tab ─────────────────────────────────────────────────────────────
function TrainingTab({ theme, isManager, isHRDirector, currentUser, onError }: any) {
  const [records, setRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState<any>(null);

  useEffect(() => { load(); }, [statusFilter]);

  async function load() {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : "";
      const d = await api(`/api/hr/training/${params}`);
      setRecords(Array.isArray(d) ? d : d?.results || []);
    } catch (e: any) { onError(e.message); }
    finally { setLoading(false); }
  }

  async function approveRecord(r: any) {
    try {
      const updated = await api(`/api/hr/training/${r.id}/approve/`, { method: "POST" });
      setRecords(p => p.map(x => x.id === updated.id ? updated : x));
    } catch (e: any) { onError(e.message); }
  }

  const filtered = records.filter(r => !search ||
    r.training_name.toLowerCase().includes(search.toLowerCase()) ||
    r.employee_name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden" }}>
      <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 8, padding: "7px 10px", flex: 1 }}>
            <Ic.Search size={13} color={theme.textDim} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search training records…"
              style={{ background: "none", border: "none", color: theme.text, fontSize: 13, outline: "none", flex: 1 }} {...spellProps} />
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            style={{ ...inputS(theme), width: 140 }}>
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="expired">Expired</option>
            <option value="waived">Waived</option>
          </select>
          <button onClick={() => setShowNew(true)} style={{ display: "flex", alignItems: "center", gap: 6, background: "#10B981", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            <Ic.Plus size={14} /> Add Record
          </button>
        </div>

        {loading ? <div style={{ color: theme.textDim, textAlign: "center", paddingTop: 40 }}>Loading…</div>
          : filtered.length === 0 ? <EmptyState theme={theme} label="Training Records" icon={<Ic.Award size={36} color="#10B981" />} />
          : filtered.map(r => (
            <div key={r.id} onClick={() => setSelected(r)}
              style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 10, marginBottom: 8, cursor: "pointer", transition: "border-color 0.15s" }}
              onMouseEnter={e => (e.currentTarget as HTMLElement).style.borderColor = theme.borderLight}
              onMouseLeave={e => (e.currentTarget as HTMLElement).style.borderColor = theme.border}
            >
              <Ic.Award size={16} color="#10B981" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: theme.text, fontSize: 14, fontWeight: 600 }}>{r.training_name}</div>
                <div style={{ color: theme.textDim, fontSize: 11, marginTop: 2 }}>
                  {r.employee_name} · {r.training_type || "General"} · {r.system_doc_number}
                  {r.completed_date && ` · Completed ${fmtDate(r.completed_date)}`}
                  {r.expiry_date && ` · Expires ${fmtDate(r.expiry_date)}`}
                </div>
              </div>
              <StatusBadge status={r.status} theme={theme} />
              <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                {isHRDirector && r.status === 'pending' && (
                  <button onClick={e => { e.stopPropagation(); approveRecord(r); }} title="Approve (mark completed)"
                    style={{ display: "flex", alignItems: "center", gap: 4, height: 30, padding: "0 10px", borderRadius: 6, background: "#10B981", border: "none", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                    ✓ Approve
                  </button>
                )}
                {r.certificate_file && (
                  <a href={`/api/hr/training/${r.id}/certificate/`} download title="Download Certificate"
                    onClick={e => e.stopPropagation()}
                    style={{ width: 30, height: 30, borderRadius: 6, background: theme.surface, border: `1px solid ${theme.border}`, color: theme.textMuted, display: "inline-flex", alignItems: "center", justifyContent: "center", textDecoration: "none" }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = "#10B981"; (e.currentTarget as HTMLElement).style.borderColor = "#10B981"; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = theme.textMuted; (e.currentTarget as HTMLElement).style.borderColor = theme.border; }}>
                    <Ic.Download size={14} />
                  </a>
                )}
              </div>
            </div>
          ))
        }
      </div>

      {showNew && (
        <NewTrainingModal theme={theme} isHRDirector={isHRDirector} onClose={() => setShowNew(false)}
          onSuccess={(r: any) => { setRecords(p => [r, ...p]); setShowNew(false); }} />
      )}
    </div>
  );
}

// ─── Shared sub-components ────────────────────────────────────────────────────

function Section({ title, children, theme }: any) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ color: theme.textMuted, fontSize: 11, fontWeight: 700, textTransform: "uppercase" as const, letterSpacing: 0.8, marginBottom: 10, paddingBottom: 6, borderBottom: `1px solid ${theme.border}` }}>{title}</div>
      {children}
    </div>
  );
}

function EmptyState({ theme, label, icon }: any) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 60, gap: 12 }}>
      {icon}
      <div style={{ color: theme.text, fontSize: 15, fontWeight: 600 }}>No {label} yet</div>
      <div style={{ color: theme.textDim, fontSize: 13 }}>Records added here will appear in this section</div>
    </div>
  );
}

function ActionBtn({ label, icon, onClick, color, theme, busy }: any) {
  return (
    <button onClick={onClick} disabled={busy} style={{ display: "flex", alignItems: "center", gap: 5, background: color, color: "#fff", border: "none", borderRadius: 7, padding: "7px 12px", fontSize: 12, fontWeight: 600, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.7 : 1 }}>
      {icon}{label}
    </button>
  );
}

// ─── New Review Modal ─────────────────────────────────────────────────────────
function NewReviewModal({ theme, currentUser, onClose, onSuccess }: any) {
  const [users, setUsers] = useState<any[]>([]);
  const [employeeId, setEmployeeId] = useState("");
  const [reviewType, setReviewType] = useState("engineer");
  const [year, setYear] = useState(new Date().getFullYear());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api("/api/hr/users/").then(setUsers).catch((e: any) => setErr(e.message));
  }, []);

  // Auto-detect review type from employee role
  const selectedUser = users.find(u => String(u.id) === String(employeeId));
  useEffect(() => {
    if (selectedUser) setReviewType(selectedUser.is_salary ? "engineer" : "technician");
  }, [selectedUser?.id]);

  const IS = inputS(theme);

  async function create() {
    if (!employeeId) { setErr("Select an employee"); return; }
    setBusy(true); setErr("");
    try {
      const r = await api("/api/hr/reviews/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ employee: employeeId, review_type: reviewType, review_year: year }),
      });
      onSuccess(r);
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 14, padding: 24, maxWidth: 440, width: "95%" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
          <Ic.Clipboard size={17} color={theme.yellow} />
          <span style={{ color: theme.text, fontSize: 15, fontWeight: 600 }}>New Employee Review</span>
          <button onClick={onClose} style={{ marginLeft: "auto", background: "none", border: "none", color: theme.textDim, fontSize: 22, cursor: "pointer" }}>×</button>
        </div>
        {err && <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid #EF4444", borderRadius: 8, padding: "9px 12px", color: "#EF4444", fontSize: 13, marginBottom: 14 }}>{err}</div>}

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", color: theme.textMuted, fontSize: 11, fontWeight: 600, marginBottom: 5, textTransform: "uppercase" as const }}>Employee <span style={{ color: theme.red }}>*</span></label>
          <select value={employeeId} onChange={e => setEmployeeId(e.target.value)} style={IS}>
            <option value="">Select employee…</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name} {u.title ? `— ${u.title}` : ""}</option>)}
          </select>
          {selectedUser && (
            <div style={{ color: theme.textDim, fontSize: 11, marginTop: 4 }}>
              {selectedUser.title && `Title: ${selectedUser.title}`}
              {selectedUser.start_date && ` · Started: ${fmtDate(selectedUser.start_date)}`}
            </div>
          )}
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", color: theme.textMuted, fontSize: 11, fontWeight: 600, marginBottom: 5, textTransform: "uppercase" as const }}>Review Type</label>
          <div style={{ display: "flex", gap: 8 }}>
            {[{ val: "engineer", label: "Engineer / Salary" }, { val: "technician", label: "Technician / Hourly" }].map(t => (
              <button key={t.val} onClick={() => setReviewType(t.val)}
                style={{ flex: 1, padding: "9px 12px", background: reviewType === t.val ? theme.yellow + "22" : theme.bg, border: `2px solid ${reviewType === t.val ? theme.yellow : theme.border}`, borderRadius: 8, color: reviewType === t.val ? theme.yellow : theme.textMuted, fontSize: 13, fontWeight: reviewType === t.val ? 600 : 400, cursor: "pointer" }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 18 }}>
          <label style={{ display: "block", color: theme.textMuted, fontSize: 11, fontWeight: 600, marginBottom: 5, textTransform: "uppercase" as const }}>Review Year</label>
          <input type="number" value={year} onChange={e => setYear(Number(e.target.value))} min={2020} max={2030} style={IS} />
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "9px 18px", background: "transparent", border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.textMuted, cursor: "pointer", fontSize: 13 }}>Cancel</button>
          <button onClick={create} disabled={busy} style={{ padding: "9px 18px", background: theme.yellow, border: "none", borderRadius: 8, color: "#000", cursor: "pointer", fontSize: 13, fontWeight: 600, opacity: busy ? 0.7 : 1 }}>
            {busy ? "Creating…" : "Create Review"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── New Training Modal ───────────────────────────────────────────────────────
function NewTrainingModal({ theme, isHRDirector, onClose, onSuccess }: any) {
  const [users, setUsers] = useState<any[]>([]);
  const [form, setForm] = useState({ employee: "", training_name: "", training_type: "", description: "", completed_date: "", expiry_date: "", trainer: "", status: "pending", notes: "" });
  const [certFile, setCertFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => { api("/api/hr/users/").then(setUsers).catch(() => {}); }, []);

  const IS = inputS(theme);
  const lbl = (text: string, req = false) => (
    <label style={{ display: "block", color: theme.textMuted, fontSize: 11, fontWeight: 600, marginBottom: 5, textTransform: "uppercase" as const, letterSpacing: 0.5 }}>
      {text}{req && <span style={{ color: theme.red }}> *</span>}
    </label>
  );

  async function save() {
    if (!form.employee || !form.training_name) { setErr(true); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      if (certFile) fd.append("certificate_file", certFile);
      const r = await api("/api/hr/training/", { method: "POST", body: fd });
      onSuccess(r);
    } catch (e: any) { console.error(e); }
    finally { setBusy(false); }
  }

  const set = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 14, padding: 24, maxWidth: 520, width: "95%", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
          <Ic.Award size={17} color="#10B981" />
          <span style={{ color: theme.text, fontSize: 15, fontWeight: 600 }}>Add Training Record</span>
          <button onClick={onClose} style={{ marginLeft: "auto", background: "none", border: "none", color: theme.textDim, fontSize: 22, cursor: "pointer" }}>×</button>
        </div>
        {err && <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid #EF4444", borderRadius: 8, padding: "9px 12px", color: "#EF4444", fontSize: 13, marginBottom: 14 }}>Employee and training name are required</div>}

        <div style={{ marginBottom: 14 }}>{lbl("Employee", true)}<select value={form.employee} onChange={e => set("employee", e.target.value)} style={IS}><option value="">Select…</option>{users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}</select></div>
        <div style={{ marginBottom: 14 }}>{lbl("Training Name", true)}<input value={form.training_name} onChange={e => set("training_name", e.target.value)} style={IS} {...spellProps} /></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div style={{ marginBottom: 14 }}>{lbl("Training Type")}<input value={form.training_type} onChange={e => set("training_type", e.target.value)} placeholder="e.g. Safety, Quality" style={IS} {...spellProps} /></div>
          <div style={{ marginBottom: 14 }}>{lbl("Trainer")}<input value={form.trainer} onChange={e => set("trainer", e.target.value)} style={IS} {...spellProps} /></div>
          <div style={{ marginBottom: 14 }}>{lbl("Completed Date")}<input type="date" value={form.completed_date} onChange={e => set("completed_date", e.target.value)} style={IS} /></div>
          <div style={{ marginBottom: 14 }}>{lbl("Expiry Date")}<input type="date" value={form.expiry_date} onChange={e => set("expiry_date", e.target.value)} style={IS} /></div>
        </div>
        <div style={{ marginBottom: 14 }}>{lbl("Status")}{isHRDirector
          ? <select value={form.status} onChange={e => set("status", e.target.value)} style={IS}><option value="pending">Pending</option><option value="completed">Completed</option><option value="waived">Waived</option></select>
          : <div style={{ ...IS, display: "flex", alignItems: "center", color: theme.textDim }}>Pending — awaiting HR approval</div>}
        </div>
        <div style={{ marginBottom: 14 }}>{lbl("Description / Notes")}<textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={2} style={{ ...IS, resize: "vertical" as const }} {...spellProps} /></div>
        <div style={{ marginBottom: 18 }}>
          {lbl("Certificate (optional)")}
          <input ref={fileRef} type="file" style={{ display: "none" }} onChange={e => setCertFile(e.target.files?.[0] || null)} />
          <div onClick={() => fileRef.current?.click()} style={{ border: `2px dashed ${certFile ? "#10B981" : theme.border}`, borderRadius: 8, padding: 16, textAlign: "center" as const, cursor: "pointer", background: certFile ? "#10B98111" : theme.bg }}>
            {certFile ? <span style={{ color: "#10B981", fontSize: 13 }}>✓ {certFile.name}</span> : <span style={{ color: theme.textDim, fontSize: 13 }}>Click to attach certificate</span>}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "9px 18px", background: "transparent", border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.textMuted, cursor: "pointer", fontSize: 13 }}>Cancel</button>
          <button onClick={save} disabled={busy} style={{ padding: "9px 18px", background: "#10B981", border: "none", borderRadius: 8, color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600, opacity: busy ? 0.7 : 1 }}>
            {busy ? "Saving…" : "Save Record"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── HR Doc Upload Modal ──────────────────────────────────────────────────────
function HRDocUploadModal({ theme, category, allowFolderChoice, onClose, onSuccess }: any) {
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [folder, setFolder] = useState(category || "hr_docs");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  async function upload() {
    if (!title.trim()) { setErr("Title is required"); return; }
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("category", allowFolderChoice ? folder : category);
      fd.append("title", title.trim());
      fd.append("description", desc);
      fd.append("status", "active");
      if (file) fd.append("file", file);
      const r = await api("/api/hr/documents/", { method: "POST", body: fd });
      onSuccess(r);
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  }

  const IS = inputS(theme);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 14, padding: 24, maxWidth: 460, width: "95%" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
          <Ic.Upload size={17} color="#3B82F6" />
          <span style={{ color: theme.text, fontSize: 15, fontWeight: 600 }}>Upload HR Document</span>
          <button onClick={onClose} style={{ marginLeft: "auto", background: "none", border: "none", color: theme.textDim, fontSize: 22, cursor: "pointer" }}>×</button>
        </div>
        {err && <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid #EF4444", borderRadius: 8, padding: "9px 12px", color: "#EF4444", fontSize: 13, marginBottom: 14 }}>{err}</div>}

        {allowFolderChoice && (
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", color: theme.textMuted, fontSize: 11, fontWeight: 600, marginBottom: 5, textTransform: "uppercase" as const }}>Folder <span style={{ color: theme.red }}>*</span></label>
            <select value={folder} onChange={e => setFolder(e.target.value)} style={IS}>
              <option value="hr_docs">Employee Documents</option>
              <option value="healthcare">Health Care</option>
              <option value="financial">Financial</option>
              <option value="other_benefits">Other Benefits</option>
            </select>
          </div>
        )}
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", color: theme.textMuted, fontSize: 11, fontWeight: 600, marginBottom: 5, textTransform: "uppercase" as const }}>Title <span style={{ color: theme.red }}>*</span></label>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Document title" style={IS} {...spellProps} />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", color: theme.textMuted, fontSize: 11, fontWeight: 600, marginBottom: 5, textTransform: "uppercase" as const }}>Description</label>
          <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={2} style={{ ...IS, resize: "vertical" as const }} {...spellProps} />
        </div>
        <div style={{ marginBottom: 18 }}>
          <label style={{ display: "block", color: theme.textMuted, fontSize: 11, fontWeight: 600, marginBottom: 5, textTransform: "uppercase" as const }}>File</label>
          <input ref={fileRef} type="file" style={{ display: "none" }} onChange={e => { const f = e.target.files?.[0]; if (f) { setFile(f); if (!title) setTitle(f.name.replace(/\.[^.]+$/, "")); } }} />
          <div onClick={() => fileRef.current?.click()}
            style={{ border: `2px dashed ${file ? "#3B82F6" : theme.border}`, borderRadius: 8, padding: 20, textAlign: "center" as const, cursor: "pointer", background: file ? "#3B82F611" : theme.bg }}>
            {file ? <span style={{ color: "#3B82F6", fontSize: 13 }}>✓ {file.name}</span> : <span style={{ color: theme.textDim, fontSize: 13 }}>Click to browse or drag & drop</span>}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "9px 18px", background: "transparent", border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.textMuted, cursor: "pointer", fontSize: 13 }}>Cancel</button>
          <button onClick={upload} disabled={busy} style={{ padding: "9px 18px", background: "#3B82F6", border: "none", borderRadius: 8, color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600, opacity: busy ? 0.7 : 1 }}>
            {busy ? "Uploading…" : "Upload"}
          </button>
        </div>
      </div>
    </div>
  );
}
